import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, BarChart3, Download, FileSpreadsheet } from "lucide-react";

// İskonto Kullanım Raporu — bu hafta hangi kullanıcı ne kadar iskonto hakkı kullandı.
// (Roller ekranından Raporlar menüsüne taşındı — Faz A.)
export default function DiscountUsageCard() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [week, setWeek] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/pos/discount/usage-report");
      setRows(r.data.rows || []); setWeek(r.data.week || "");
    } catch (err) { /* yetki yoksa sessiz */ }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line

  const exportReport = async (fmt) => {
    try {
      const r = await api.get(`/pos/discount/usage-report/export?format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `iskonto-kullanim-${week || "rapor"}.${fmt === "xlsx" ? "xlsx" : "csv"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(`Rapor indirildi (${fmt === "xlsx" ? "Excel" : "CSV"})`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Dışa aktarma başarısız"); }
  };

  return (
    <div className="rounded-md bg-white ring-1 ring-inset ring-emerald-300/40" data-testid="discount-usage-card">
      <div className="flex w-full items-center justify-between px-4 py-3">
        <button data-testid="discount-usage-toggle" onClick={() => setOpen((o) => !o)}
          className="flex flex-1 items-center gap-2 text-left">
          <BarChart3 className="h-4 w-4 text-emerald-600" />
          <span className="font-heading text-sm font-bold text-slate-800">İskonto Kullanım Raporu</span>
          <span className="hidden text-xs text-slate-400 sm:inline">— bu hafta kim ne kadar iskonto hakkı kullandı</span>
        </button>
        <div className="flex items-center gap-2">
          {open && rows.length > 0 && (
            <>
              <button data-testid="discount-usage-export-csv" onClick={() => exportReport("csv")}
                className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50">
                <Download className="h-3.5 w-3.5" /> CSV
              </button>
              <button data-testid="discount-usage-export-xlsx" onClick={() => exportReport("xlsx")}
                className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100">
                <FileSpreadsheet className="h-3.5 w-3.5" /> Excel
              </button>
            </>
          )}
          <button onClick={() => setOpen((o) => !o)} className="text-xs font-semibold text-emerald-600">{open ? "Gizle" : "Göster"}</button>
        </div>
      </div>
      {open && (
        <div className="border-t border-slate-100 p-4">
          {loading ? (
            <p className="py-4 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></p>
          ) : rows.length === 0 ? (
            <p className="py-3 text-center text-sm text-slate-400">Bu hafta iskonto yetkili kullanıcı kaydı yok.</p>
          ) : (
            <>
              <p className="mb-2 text-[11px] text-slate-400">İş haftası: {week} · haklar her hafta sıfırlanır. <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[9px] font-bold text-sky-700">ÖZEL</span> = kullanıcıya özel tanımlı yetki.</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
                      <th className="py-2 pr-3">Kullanıcı</th><th className="py-2 pr-3">Rol</th>
                      <th className="py-2 pr-3">Maks.%</th><th className="py-2 pr-3">Kullanım</th><th className="py-2 pr-3">Kalan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((x, i) => {
                      const pct = x.weekly_limit > 0 ? Math.min(100, Math.round((x.used / x.weekly_limit) * 100)) : 0;
                      return (
                        <tr key={i} data-testid={`discount-usage-row-${i}`} className="border-b border-slate-50">
                          <td className="py-2 pr-3 font-medium text-slate-700">{x.name} {x.source === "user" && <span className="ml-1 rounded bg-sky-100 px-1.5 py-0.5 text-[9px] font-bold text-sky-700">ÖZEL</span>}</td>
                          <td className="py-2 pr-3 text-slate-500">{x.role_label}</td>
                          <td className="py-2 pr-3 tabular">%{x.max_pct}</td>
                          <td className="py-2 pr-3">
                            <div className="flex items-center gap-2">
                              <div className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-100"><div className={`h-full ${pct >= 100 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} /></div>
                              <span className="tabular text-xs text-slate-500">{x.used}/{x.weekly_limit}</span>
                            </div>
                          </td>
                          <td className={`py-2 pr-3 tabular font-semibold ${x.remaining === 0 ? "text-red-600" : "text-emerald-700"}`}>{x.remaining}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
